import {apiFetch} from '~/composables/api/apiFetch';
import {
  buildIntegrationConnectionPath,
  resolveIntegrationConnectionSlug,
} from '~/global/integrations';
import {
  applyIntegrationConnectionCheckAbort,
  applyIntegrationConnectionCheckResult,
  createResolvedIntegrationConnectionEntry,
  createUnknownIntegrationConnectionEntry,
  getNextIntegrationConnectionRetryDelayMs,
  hasResolvedIntegrationConnectionEntry,
  normalizeIntegrationConnectionEntry,
  shouldRefreshIntegrationConnectionEntry,
  type IntegrationConnectionCacheEntry,
  type IntegrationConnectionStatus,
} from '~/utils/integrationConnectionCacheState';
import {useActiveOrg} from '~/composables/organization/useActiveOrg';

interface PersistedIntegrationConnectionCache {
  version: number;
  entries: Record<string, IntegrationConnectionCacheEntry>;
}

interface EnsureConnectionStatusesOptions {
  awaitFresh?: boolean;
  forceRefresh?: boolean;
}

interface IntegrationConnectionCacheStorage {
  getItem(_key: string): string | null;
  removeItem(_key: string): void;
  setItem(_key: string, _value: string): void;
}

const INTEGRATION_CONNECTION_CACHE_STORAGE_PREFIX = 'vector.integration-connection-cache';
const INTEGRATION_CONNECTION_CACHE_STORAGE_VERSION = 1;
const INTEGRATION_CONNECTION_CACHE_DEBUG_STORAGE_KEY = 'vector.debug.integration-connection-cache';
const INTEGRATION_CONNECTION_REQUEST_TIMEOUT_MS = 15000;
const INTEGRATION_CONNECTION_ABORT_REASON_MANUAL = 'manual-state-update';
const INTEGRATION_CONNECTION_ABORT_REASON_ORG_SWITCH = 'org-switch';
const INTEGRATION_CONNECTION_ABORT_REASON_TIMEOUT = 'request-timeout';

const inFlightRefreshes = new Map<string, Promise<void>>();
const inFlightRefreshControllers = new Map<string, AbortController>();
const inFlightRefreshTokens = new Map<string, symbol>();
const scheduledRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();

let hydratePromise: Promise<string | null> | null = null;

const getStorageKey = (activeOrgId: string) => {
  return `${INTEGRATION_CONNECTION_CACHE_STORAGE_PREFIX}.v${INTEGRATION_CONNECTION_CACHE_STORAGE_VERSION}.${activeOrgId}`;
};

const getConnectionCacheStorage = (): IntegrationConnectionCacheStorage | null => {
  if (!import.meta.client) {
    return null;
  }

  try {
    return sessionStorage;
  } catch {
    return null;
  }
};

const normalizeRequestedSlugs = (slugs: string[]): string[] => {
  return Array.from(
    new Set(
      slugs
        .map(slug => slug.trim())
        .filter(Boolean)
        .map(resolveIntegrationConnectionSlug)
    )
  );
};

const isIntegrationConnectionCacheDebugEnabled = (): boolean => {
  if (!import.meta.client) {
    return false;
  }

  try {
    const hasDebugQueryParam =
      new URLSearchParams(window.location.search).get('debugIntegrationConnectionCache') === 'true';
    const hasDebugStorageFlag =
      localStorage.getItem(INTEGRATION_CONNECTION_CACHE_DEBUG_STORAGE_KEY) === 'true';

    return hasDebugQueryParam || hasDebugStorageFlag;
  } catch {
    return false;
  }
};

const formatEntryForLog = (entry: IntegrationConnectionCacheEntry | undefined) => {
  if (!entry) {
    return null;
  }

  return {
    status: entry.status,
    lastCheckedAt: entry.lastCheckedAt,
    lastConfirmedAt: entry.lastConfirmedAt,
    expiresAt: entry.expiresAt,
    revalidationFailures: entry.revalidationFailures,
    firstFailureAt: entry.firstFailureAt,
    lastFailureAt: entry.lastFailureAt,
  };
};

const debugIntegrationConnectionCache = (message: string, payload?: Record<string, unknown>) => {
  if (!isIntegrationConnectionCacheDebugEnabled()) {
    return;
  }

  console.debug('[IntegrationConnectionCache]', message, payload ?? {});
};

const clearPersistedEntries = (activeOrgId: string) => {
  const storage = getConnectionCacheStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(getStorageKey(activeOrgId));
    debugIntegrationConnectionCache('Cleared persisted cache', {activeOrgId});
  } catch (error) {
    debugIntegrationConnectionCache('Failed to clear persisted cache', {
      activeOrgId,
      error:
        error instanceof Error
          ? {name: error.name, message: error.message}
          : {message: 'Unknown error'},
    });
  }
};

const readPersistedEntries = (
  activeOrgId: string
): Record<string, IntegrationConnectionCacheEntry> => {
  const storage = getConnectionCacheStorage();
  if (!storage) {
    return {};
  }

  const storageKey = getStorageKey(activeOrgId);

  try {
    const stored = storage.getItem(storageKey);
    if (!stored) {
      debugIntegrationConnectionCache('No persisted cache found', {activeOrgId});
      return {};
    }

    const parsed = JSON.parse(stored) as Partial<PersistedIntegrationConnectionCache>;
    if (
      !parsed ||
      parsed.version !== INTEGRATION_CONNECTION_CACHE_STORAGE_VERSION ||
      !parsed.entries ||
      typeof parsed.entries !== 'object'
    ) {
      clearPersistedEntries(activeOrgId);
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed.entries).map(([slug, entry]) => [
        slug,
        normalizeIntegrationConnectionEntry(entry),
      ])
    );
  } catch {
    clearPersistedEntries(activeOrgId);
    debugIntegrationConnectionCache('Failed to read persisted cache', {activeOrgId});
    return {};
  }
};

const writePersistedEntries = (
  activeOrgId: string,
  entries: Record<string, IntegrationConnectionCacheEntry>
) => {
  const storage = getConnectionCacheStorage();
  if (!storage) {
    return;
  }

  const payload: PersistedIntegrationConnectionCache = {
    version: INTEGRATION_CONNECTION_CACHE_STORAGE_VERSION,
    entries,
  };

  try {
    storage.setItem(getStorageKey(activeOrgId), JSON.stringify(payload));
  } catch (error) {
    debugIntegrationConnectionCache('Failed to persist cache', {
      activeOrgId,
      error:
        error instanceof Error
          ? {name: error.name, message: error.message}
          : {message: 'Unknown error'},
    });
  }
};

const getRefreshKey = (activeOrgId: string, slug: string) => `${activeOrgId}:${slug}`;

export const useIntegrationConnectionCache = () => {
  const activeOrgId = useState<string | null>('integrationConnectionCache:activeOrgId', () => null);
  const entries = useState<Record<string, IntegrationConnectionCacheEntry>>(
    'integrationConnectionCache:entries',
    () => ({})
  );
  const loadingEntries = useState<Record<string, boolean>>(
    'integrationConnectionCache:loadingEntries',
    () => ({})
  );
  const isHydrated = useState<boolean>('integrationConnectionCache:isHydrated', () => false);

  const setLoading = (canonicalSlug: string, isLoading: boolean) => {
    loadingEntries.value = {
      ...loadingEntries.value,
      [canonicalSlug]: isLoading,
    };
  };

  const clearRetryTimer = (orgId: string, canonicalSlug: string) => {
    const key = getRefreshKey(orgId, canonicalSlug);
    const timer = scheduledRetryTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      scheduledRetryTimers.delete(key);
    }
  };

  const clearRetryTimersForOrg = (orgId: string | null) => {
    if (!orgId) {
      return;
    }

    const orgPrefix = `${orgId}:`;
    for (const [key, timer] of scheduledRetryTimers.entries()) {
      if (key.startsWith(orgPrefix)) {
        clearTimeout(timer);
        scheduledRetryTimers.delete(key);
      }
    }
  };

  const clearInFlightRefreshesForOrg = (orgId: string | null) => {
    if (!orgId) {
      return;
    }

    const orgPrefix = `${orgId}:`;
    for (const [key, controller] of inFlightRefreshControllers.entries()) {
      if (key.startsWith(orgPrefix)) {
        controller.abort(INTEGRATION_CONNECTION_ABORT_REASON_ORG_SWITCH);
        inFlightRefreshControllers.delete(key);
      }
    }

    for (const key of inFlightRefreshes.keys()) {
      if (key.startsWith(orgPrefix)) {
        inFlightRefreshes.delete(key);
        inFlightRefreshTokens.delete(key);
      }
    }
  };

  const abortInFlightRefresh = (orgId: string, canonicalSlug: string) => {
    const refreshKey = getRefreshKey(orgId, canonicalSlug);
    const controller = inFlightRefreshControllers.get(refreshKey);
    if (!controller) {
      return;
    }

    controller.abort(INTEGRATION_CONNECTION_ABORT_REASON_MANUAL);
    inFlightRefreshControllers.delete(refreshKey);
    inFlightRefreshes.delete(refreshKey);
    inFlightRefreshTokens.delete(refreshKey);
    setLoading(canonicalSlug, false);
    debugIntegrationConnectionCache('Aborted in-flight refresh', {
      activeOrgId: orgId,
      canonicalSlug,
      reason: INTEGRATION_CONNECTION_ABORT_REASON_MANUAL,
    });
  };

  const persistEntries = (orgId: string) => {
    writePersistedEntries(orgId, entries.value);
  };

  const updateEntry = (
    orgId: string,
    canonicalSlug: string,
    nextEntry: IntegrationConnectionCacheEntry
  ) => {
    const previousEntry = entries.value[canonicalSlug];
    entries.value = {
      ...entries.value,
      [canonicalSlug]: nextEntry,
    };
    persistEntries(orgId);
    debugIntegrationConnectionCache('Updated cached connection entry', {
      activeOrgId: orgId,
      canonicalSlug,
      previousEntry: formatEntryForLog(previousEntry),
      nextEntry: formatEntryForLog(nextEntry),
    });
  };

  const getEntry = (slug: string): IntegrationConnectionCacheEntry | undefined => {
    return entries.value[resolveIntegrationConnectionSlug(slug)];
  };

  const getResolvedActiveOrgId = async (): Promise<string | null> => {
    if (!import.meta.client) {
      return null;
    }

    const {$propelAuth} = useNuxtApp();
    if (!$propelAuth) {
      return null;
    }

    const {getActiveOrg} = await useActiveOrg();
    return getActiveOrg();
  };

  const scheduleRetryIfNeeded = (orgId: string, canonicalSlug: string) => {
    if (!import.meta.client) {
      return;
    }

    clearRetryTimer(orgId, canonicalSlug);

    const entry = entries.value[canonicalSlug];
    const retryDelay = getNextIntegrationConnectionRetryDelayMs(entry, Date.now());
    if (retryDelay === null) {
      debugIntegrationConnectionCache('No retry needed', {
        activeOrgId: orgId,
        canonicalSlug,
        entry: formatEntryForLog(entry),
      });
      return;
    }

    if (retryDelay <= 0) {
      debugIntegrationConnectionCache('Retrying immediately', {
        activeOrgId: orgId,
        canonicalSlug,
        entry: formatEntryForLog(entry),
      });
      void refreshConnectionStatuses([canonicalSlug], {forceRefresh: true});
      return;
    }

    const timer = setTimeout(() => {
      scheduledRetryTimers.delete(getRefreshKey(orgId, canonicalSlug));
      debugIntegrationConnectionCache('Running scheduled retry', {
        activeOrgId: orgId,
        canonicalSlug,
      });
      void refreshConnectionStatuses([canonicalSlug], {forceRefresh: true});
    }, retryDelay);

    scheduledRetryTimers.set(getRefreshKey(orgId, canonicalSlug), timer);
    debugIntegrationConnectionCache('Scheduled retry', {
      activeOrgId: orgId,
      canonicalSlug,
      retryDelayMs: retryDelay,
      entry: formatEntryForLog(entry),
    });
  };

  const syncScheduledRetries = (orgId: string) => {
    clearRetryTimersForOrg(orgId);
    Object.keys(entries.value).forEach(canonicalSlug => {
      scheduleRetryIfNeeded(orgId, canonicalSlug);
    });
  };

  const ensureHydrated = async (): Promise<string | null> => {
    if (!import.meta.client) {
      return null;
    }

    if (hydratePromise) {
      return hydratePromise;
    }

    hydratePromise = (async () => {
      const resolvedActiveOrgId = await getResolvedActiveOrgId();

      if (!resolvedActiveOrgId) {
        clearRetryTimersForOrg(activeOrgId.value);
        clearInFlightRefreshesForOrg(activeOrgId.value);
        activeOrgId.value = null;
        entries.value = {};
        loadingEntries.value = {};
        isHydrated.value = true;
        debugIntegrationConnectionCache('Hydration skipped because no active org was resolved');
        return null;
      }

      if (isHydrated.value && activeOrgId.value === resolvedActiveOrgId) {
        debugIntegrationConnectionCache('Reused hydrated cache state', {
          activeOrgId: resolvedActiveOrgId,
          entryCount: Object.keys(entries.value).length,
        });
        return resolvedActiveOrgId;
      }

      clearRetryTimersForOrg(activeOrgId.value);
      clearInFlightRefreshesForOrg(activeOrgId.value);

      activeOrgId.value = resolvedActiveOrgId;
      entries.value = readPersistedEntries(resolvedActiveOrgId);
      loadingEntries.value = {};
      isHydrated.value = true;

      syncScheduledRetries(resolvedActiveOrgId);
      debugIntegrationConnectionCache('Hydrated cache state', {
        activeOrgId: resolvedActiveOrgId,
        entryCount: Object.keys(entries.value).length,
        cachedSlugs: Object.keys(entries.value),
      });

      return resolvedActiveOrgId;
    })().finally(() => {
      hydratePromise = null;
    });

    return hydratePromise;
  };

  const refreshCanonicalConnectionStatus = async (
    orgId: string,
    canonicalSlug: string,
    forceRefresh: boolean = false
  ) => {
    const refreshKey = getRefreshKey(orgId, canonicalSlug);
    const existingRefresh = inFlightRefreshes.get(refreshKey);
    if (existingRefresh) {
      debugIntegrationConnectionCache('Joined in-flight refresh', {
        activeOrgId: orgId,
        canonicalSlug,
      });
      return existingRefresh;
    }

    const currentEntry = entries.value[canonicalSlug];
    if (!shouldRefreshIntegrationConnectionEntry(currentEntry, Date.now(), forceRefresh)) {
      debugIntegrationConnectionCache('Skipped refresh because cache is still valid', {
        activeOrgId: orgId,
        canonicalSlug,
        forceRefresh,
        entry: formatEntryForLog(currentEntry),
      });
      scheduleRetryIfNeeded(orgId, canonicalSlug);
      return Promise.resolve();
    }

    const controller = new AbortController();
    const refreshToken = Symbol(refreshKey);
    const refreshPromise = (async () => {
      const timeoutId = setTimeout(
        () => controller.abort(INTEGRATION_CONNECTION_ABORT_REASON_TIMEOUT),
        INTEGRATION_CONNECTION_REQUEST_TIMEOUT_MS
      );
      setLoading(canonicalSlug, true);
      debugIntegrationConnectionCache('Starting refresh', {
        activeOrgId: orgId,
        canonicalSlug,
        forceRefresh,
        entryBeforeRefresh: formatEntryForLog(entries.value[canonicalSlug]),
        timeoutMs: INTEGRATION_CONNECTION_REQUEST_TIMEOUT_MS,
      });

      try {
        const connection = await apiFetch(buildIntegrationConnectionPath(canonicalSlug), {
          signal: controller.signal,
        });
        if (activeOrgId.value !== orgId) {
          debugIntegrationConnectionCache('Ignored refresh result after org change', {
            requestOrgId: orgId,
            currentOrgId: activeOrgId.value,
            canonicalSlug,
          });
          return;
        }

        const latestEntry = entries.value[canonicalSlug];
        const nextEntry = applyIntegrationConnectionCheckResult(
          latestEntry,
          connection === true,
          Date.now()
        );
        updateEntry(orgId, canonicalSlug, nextEntry);
        debugIntegrationConnectionCache('Refresh completed', {
          activeOrgId: orgId,
          canonicalSlug,
          confirmedConnected: connection === true,
          entryAfterRefresh: formatEntryForLog(nextEntry),
        });
      } catch (error: unknown) {
        const isAbortError = error instanceof Error && error.name === 'AbortError';
        const abortReason = controller.signal.aborted ? controller.signal.reason : null;

        if (!isAbortError) {
          console.error(error);
        }

        if (activeOrgId.value !== orgId) {
          debugIntegrationConnectionCache('Ignored refresh failure after org change', {
            requestOrgId: orgId,
            currentOrgId: activeOrgId.value,
            canonicalSlug,
          });
          return;
        }

        if (isAbortError && abortReason !== INTEGRATION_CONNECTION_ABORT_REASON_TIMEOUT) {
          debugIntegrationConnectionCache('Ignored aborted refresh', {
            activeOrgId: orgId,
            canonicalSlug,
            reason: abortReason,
          });
          return;
        }

        const latestEntry = entries.value[canonicalSlug];
        const nextEntry = isAbortError
          ? applyIntegrationConnectionCheckAbort(latestEntry, Date.now())
          : applyIntegrationConnectionCheckResult(latestEntry, false, Date.now());
        updateEntry(orgId, canonicalSlug, nextEntry);
        debugIntegrationConnectionCache('Refresh failed; applied fallback result', {
          activeOrgId: orgId,
          canonicalSlug,
          error:
            error instanceof Error
              ? {name: error.name, message: error.message}
              : {message: 'Unknown error'},
          entryAfterFailure: formatEntryForLog(nextEntry),
        });
      } finally {
        clearTimeout(timeoutId);
        const isCurrentRefresh = inFlightRefreshTokens.get(refreshKey) === refreshToken;
        if (isCurrentRefresh) {
          inFlightRefreshTokens.delete(refreshKey);
          if (inFlightRefreshControllers.get(refreshKey) === controller) {
            inFlightRefreshControllers.delete(refreshKey);
          }

          inFlightRefreshes.delete(refreshKey);

          const currentOrgId = activeOrgId.value;
          const hasCurrentOrgRefresh =
            currentOrgId !== null &&
            inFlightRefreshes.has(getRefreshKey(currentOrgId, canonicalSlug));

          if (currentOrgId !== orgId || !hasCurrentOrgRefresh) {
            // Clear stale loading state when an old org finishes after a switch,
            // but avoid stomping a same-slug refresh already running for the current org.
            setLoading(canonicalSlug, false);
          }

          if (currentOrgId === orgId && !hasCurrentOrgRefresh) {
            scheduleRetryIfNeeded(orgId, canonicalSlug);
          }
        }
      }
    })();

    inFlightRefreshControllers.set(refreshKey, controller);
    inFlightRefreshTokens.set(refreshKey, refreshToken);
    inFlightRefreshes.set(refreshKey, refreshPromise);
    return refreshPromise;
  };

  async function ensureConnectionStatuses(
    slugs: string[],
    options: EnsureConnectionStatusesOptions = {}
  ): Promise<Record<string, IntegrationConnectionStatus>> {
    const orgId = await ensureHydrated();
    if (!orgId) {
      debugIntegrationConnectionCache('Unable to ensure connection statuses without active org', {
        requestedSlugs: slugs,
      });
      return Object.fromEntries(
        slugs.map(slug => [slug, createUnknownIntegrationConnectionEntry().status])
      );
    }

    const canonicalSlugs = normalizeRequestedSlugs(slugs);
    const shouldAwaitFresh = options.awaitFresh ?? false;
    const blockingRefreshes: Promise<void>[] = [];
    debugIntegrationConnectionCache('Ensuring connection statuses', {
      activeOrgId: orgId,
      requestedSlugs: slugs,
      canonicalSlugs,
      shouldAwaitFresh,
      forceRefresh: options.forceRefresh === true,
    });

    canonicalSlugs.forEach(canonicalSlug => {
      const entry = entries.value[canonicalSlug];
      const shouldRefresh = shouldRefreshIntegrationConnectionEntry(
        entry,
        Date.now(),
        options.forceRefresh === true
      );

      if (!shouldRefresh) {
        scheduleRetryIfNeeded(orgId, canonicalSlug);
        return;
      }

      const refreshPromise = refreshCanonicalConnectionStatus(
        orgId,
        canonicalSlug,
        options.forceRefresh === true
      );

      if (!hasResolvedIntegrationConnectionEntry(entry) || shouldAwaitFresh) {
        blockingRefreshes.push(refreshPromise);
      }
    });

    if (blockingRefreshes.length > 0) {
      await Promise.allSettled(blockingRefreshes);
    }

    const resolvedStatuses = Object.fromEntries(
      slugs.map(slug => [slug, getConnectionStatus(slug)])
    );
    debugIntegrationConnectionCache('Resolved connection statuses', {
      activeOrgId: orgId,
      resolvedStatuses,
    });
    return resolvedStatuses;
  }

  async function refreshConnectionStatuses(
    slugs: string[],
    options: EnsureConnectionStatusesOptions = {}
  ) {
    return ensureConnectionStatuses(slugs, {
      ...options,
      awaitFresh: true,
      forceRefresh: options.forceRefresh ?? true,
    });
  }

  const getConnectionStatus = (slug: string): IntegrationConnectionStatus => {
    return getEntry(slug)?.status ?? 'unknown';
  };

  const hasResolvedConnectionStatus = (slug: string): boolean => {
    return hasResolvedIntegrationConnectionEntry(getEntry(slug));
  };

  const isConnected = (slug: string): boolean => {
    return getConnectionStatus(slug) === 'connected';
  };

  const isDisconnected = (slug: string): boolean => {
    return getConnectionStatus(slug) === 'disconnected';
  };

  const isLoadingConnectionStatus = (slug: string): boolean => {
    return loadingEntries.value[resolveIntegrationConnectionSlug(slug)] === true;
  };

  const setConnectionStatus = async (slug: string, isConnectedValue: boolean) => {
    const orgId = await ensureHydrated();
    if (!orgId) {
      return;
    }

    const canonicalSlug = resolveIntegrationConnectionSlug(slug);
    const nextEntry = createResolvedIntegrationConnectionEntry(
      isConnectedValue,
      Date.now(),
      entries.value[canonicalSlug]
    );
    clearRetryTimer(orgId, canonicalSlug);
    abortInFlightRefresh(orgId, canonicalSlug);
    updateEntry(orgId, canonicalSlug, nextEntry);
    debugIntegrationConnectionCache('Manually set connection status', {
      activeOrgId: orgId,
      slug,
      canonicalSlug,
      isConnectedValue,
      entryAfterManualUpdate: formatEntryForLog(nextEntry),
    });
  };

  const expireConnectionStatus = async (slug: string) => {
    const orgId = await ensureHydrated();
    if (!orgId) {
      return;
    }

    const canonicalSlug = resolveIntegrationConnectionSlug(slug);
    const currentEntry = entries.value[canonicalSlug];
    if (!currentEntry) {
      return;
    }

    clearRetryTimer(orgId, canonicalSlug);
    abortInFlightRefresh(orgId, canonicalSlug);
    updateEntry(orgId, canonicalSlug, {
      ...currentEntry,
      expiresAt: 0,
    });
    debugIntegrationConnectionCache('Expired cached connection entry', {
      activeOrgId: orgId,
      slug,
      canonicalSlug,
      entryBeforeExpire: formatEntryForLog(currentEntry),
    });

    void ensureConnectionStatuses([canonicalSlug]);
  };

  return {
    activeOrgId,
    entries,
    loadingEntries,
    isHydrated,
    ensureHydrated,
    ensureConnectionStatuses,
    refreshConnectionStatuses,
    getConnectionStatus,
    hasResolvedConnectionStatus,
    isLoadingConnectionStatus,
    isConnected,
    isDisconnected,
    setConnectionStatus,
    expireConnectionStatus,
  };
};
